import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Header from '../shared/TailwindComponents/Header';
import FilterBarWithTabs, { ListViewMode } from '../shared/TailwindComponents/FilterBarWithTabs';
import StageBoard from '../shared/board/StageBoard';
import ProjectBoardCard from './ProjectBoardCard';
import Table from '../shared/TailwindComponents/Table';
import Badge from '../shared/TailwindComponents/Badge';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import {
  PiTrashLight,
  PiFolderOpenLight,
  PiPlusLight,
  PiClockCounterClockwiseLight,
} from 'react-icons/pi';
import { showModal, hideModal } from '../../store/actions/modalActions';
import { showSidePanel, hideSidePanel } from '../../store/actions/sidePanelActions';
import {
  fetchProjects,
  deleteProject,
  fetchProjectBoardStages,
  updateProjectBoardStage,
  PROJECT_BOARD_STAGES,
} from '../../store/actions/Projects/ProjectActions';
import CreateProjectForm from './CreateProjectForm';
import AuditLogPanel from '../AuditLog/AuditLogPanel';
import DeleteProjectConfirmation from './DeleteProjectConfirmation';
import { formatDateTime } from '../../utils/functions';
import debounce from 'lodash.debounce';
import CollaboratorStack from '../shared/access/CollaboratorStack';
import clsx from 'clsx';
import { FeatureTour, projectsListOrientationSteps } from '../shared/FeatureTour';
import { useFeatureTour } from '../../hooks/useFeatureTour';

import ActivityDrawer from '../shared/activity/ActivityDrawer';
import ProfileImage from '../Header/ProfileImage';

// ─── Config ─────────────────────────────────────────────────────

/** First letters of a display name, for the avatar's initials fallback. */
const initialsOf = (name: string): string =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('');

const statusColorMap: Record<string, BadgeColor> = {
  active: 'green',
  archived: 'gray',
  completed: 'blue',
};

const statusLabelMap: Record<string, string> = {
  active: 'Active',
  archived: 'Archived',
  completed: 'Completed',
};

// ─── Component ──────────────────────────────────────────────────

const ProjectsList: React.FC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();

  const { items, totalItems, isLoading } = useSelector((state: RootState) => state.projects);

  const queryParams = new URLSearchParams(location.search);
  const [isCreating, setIsCreating] = useState(() => queryParams.get('add') === 'true');
  const [showAuditPanel, setShowAuditPanel] = useState(false);
  const [auditProjectUuid, setAuditProjectUuid] = useState('');
  const [auditProjectTitle, setAuditProjectTitle] = useState('');

  const [viewMode, setViewMode] = useState<ListViewMode>('table');
  /**
   * Board columns. Seeded with the contract's four so the board paints before
   * `/projects/stages` answers (and still works if it never does).
   */
  const [boardStages, setBoardStages] = useState<string[]>([...PROJECT_BOARD_STAGES]);

  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string[] }>({});
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const {
    isRunning: isProjectsListTourRunning,
    handleJoyrideCallback: handleProjectsListTourCallback,
  } = useFeatureTour({
    tourId: 'projects-list-orientation',
    steps: projectsListOrientationSteps,
    readyToShow: !isLoading,
    autoStartDelay: 600,
  });

  // ─── Data Fetching ───────────────────────────────────────────

  const statusFilter = selectedFilters['status']?.[0] || '';

  const fetchPage = useCallback(
    (page: number) => {
      dispatch(fetchProjects({
        page, page_size: 20,
        sort_by: sortBy, sort_order: sortOrder,
        ...(searchTerm ? { search_term: searchTerm } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
      }));
    },
    [dispatch, sortBy, sortOrder, searchTerm, statusFilter]
  );

  useEffect(() => {
    if (!isCreating) fetchPage(1);
  }, [fetchPage, isCreating]);

  const debouncedSearch = useCallback(debounce((term: string) => { setSearchTerm(term); }, 300), []);

  /* The columns, asked for once - the board renders on the contract's four
     until (and if) the endpoint answers with its own. */
  useEffect(() => {
    let cancelled = false;
    dispatch(fetchProjectBoardStages()).then((stages) => {
      if (!cancelled && Array.isArray(stages) && stages.length > 0) setBoardStages(stages);
    });
    return () => { cancelled = true; };
  }, [dispatch]);

  const boardColumns = useMemo(
    () => boardStages.map((stage) => ({ id: stage, label: t(stage) })),
    [boardStages, t]
  );

  const handleBoardMove = useCallback(
    async (project: any, stage: string) => dispatch(updateProjectBoardStage(project.uuid, stage)),
    [dispatch]
  );

  // ─── Handlers ─────────────────────────────────────────────────

  const handleCreateClose = useCallback((newUuid?: string) => {
    setIsCreating(false);
    history.replace('/projects');
    if (newUuid) { history.push(`/projects/${newUuid}`); }
    else { fetchPage(1); }
  }, [history, fetchPage]);

  const handleDelete = (projectUuid: string, projectTitle: string) => {
    dispatch(showModal({
      showModal: true,
      type: 'custom',
      message: t('Delete Project'),
      subMsg: t('This action cannot be undone.'),
      icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
      width: '500px',
      content: (
        <DeleteProjectConfirmation
          projectName={projectTitle}
          onConfirm={async () => {
            await dispatch(deleteProject(projectUuid));
            setSelectedIds((prev) => prev.filter((id) => id !== projectUuid));
            dispatch(hideModal());
          }}
          onCancel={() => dispatch(hideModal())}
        />
      ),
      onCancel: () => {},
    }));
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return;
    dispatch(showModal({
      showModal: true,
      type: 'custom',
      message: t(selectedIds.length > 1 ? 'Delete {{count}} projects?' : 'Delete this project?', { count: selectedIds.length }),
      subMsg: t('This action cannot be undone.'),
      icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
      width: '500px',
      content: (
        <DeleteProjectConfirmation
          projectCount={selectedIds.length}
          onConfirm={async () => {
            for (const id of selectedIds) { await dispatch(deleteProject(id)); }
            setSelectedIds([]);
            dispatch(hideModal());
          }}
          onCancel={() => dispatch(hideModal())}
        />
      ),
      onCancel: () => {},
    }));
  };

  const handleSortChange = (sortKey: string, order: 'asc' | 'desc') => {
    const sortFieldMap: Record<string, string> = { 'Name': 'title', 'Status': 'status', 'Resources': 'resource_count', 'Created': 'created_at' };
    setSortBy(sortFieldMap[sortKey] || 'created_at');
    setSortOrder(order);
  };

  const handleFilterChange = (filterName: string, selectedValues: string[]) => {
    setSelectedFilters((prev) => ({ ...prev, [filterName]: selectedValues }));
  };

  const handleClearAllFilters = () => {
    setSelectedFilters({});
    setSearchTerm('');
  };

  // ─── Creation form ───────────────────────────────────────────

  if (isCreating) {
    return (
      <div className="flex flex-col overflow-hidden relative h-screen">
        <CreateProjectForm onClose={handleCreateClose} />
      </div>
    );
  }

  // ─── Filter config ────────────────────────────────────────────

  const filters = [
    {
      id: 'status',
      name: t('Status'),
      options: [
        { value: 'active', label: t('Active') },
        { value: 'archived', label: t('Archived') },
        { value: 'completed', label: t('Completed') },
      ],
    },
  ];

  const sortOptions = [
    { value: 'created_at_desc', label: t('Newest First'), current: sortBy === 'created_at' && sortOrder === 'desc' },
    { value: 'created_at_asc', label: t('Oldest First'), current: sortBy === 'created_at' && sortOrder === 'asc' },
    { value: 'title_asc', label: t('Name (A-Z)'), current: sortBy === 'title' && sortOrder === 'asc' },
    { value: 'title_desc', label: t('Name (Z-A)'), current: sortBy === 'title' && sortOrder === 'desc' },
  ];

  const handleSortOptionChange = (value: string) => {
    const [field, order] = value.split('_');
    const fieldMap: Record<string, string> = { created: 'created_at', title: 'title' };
    setSortBy(field === 'created' ? 'created_at' : fieldMap[field] || field);
    setSortOrder((order === 'asc' ? 'asc' : 'desc') as 'asc' | 'desc');
  };

  // ─── Filter bar actions ───────────────────────────────────────

  const filterActions = [
    ...(selectedIds.length > 0
      ? [{
          label: t('Delete ({{count}})', { count: selectedIds.length }),
          onClick: handleBulkDelete,
          icon: <PiTrashLight className="w-4 h-4" />,
        }]
      : []),
  ];

  // ─── Table ────────────────────────────────────────────────────

  const columns = ['Name', 'Status', 'Resources', 'Access', 'Owner', 'Created', 'Actions'];

  const tableData = items.map((item: any) => ({
    id: item.uuid,
    uuid: item.uuid,
    _title: item.title || '',
    'Name': (
      <span className="text-fg-strong truncate">{item.title || t('Untitled')}</span>
    ),
    'Status': (
      <Badge color={statusColorMap[item.status] || 'gray'}>
        {statusLabelMap[item.status] || item.status}
      </Badge>
    ),
    'Resources': (
      <span className="text-sm text-fg-body">{item.resource_count || 0}</span>
    ),
    'Access': item.has_access && item.has_access.length > 0 ? (
      <CollaboratorStack collaborators={item.has_access} max={5} />
    ) : (
      <span className="text-xs text-fg-muted">—</span>
    ),
    'Owner': item.owner?.name ? (
      <div className="flex items-center gap-2 min-w-0">
        <ProfileImage
          profilePicture={item.owner.profile_picture || null}
          email={item.owner.email || null}
          initials={initialsOf(item.owner.name)}
          name={item.owner.name}
          size="w-6 h-6"
        />
        <span className="text-sm text-fg-body truncate">{item.owner.name}</span>
      </div>
    ) : (
      <span className="text-xs text-fg-muted">—</span>
    ),
    'Created': (
      <span className="text-sm text-fg-muted">{formatDateTime(item.created_at)}</span>
    ),
  }));

  const tableActions = [
    {
      label: t('Activity'),
      onClick: (row: any) => {
        setAuditProjectUuid(row.uuid);
        setAuditProjectTitle(row._title);
        setShowAuditPanel(true);
      },
      icon: <PiClockCounterClockwiseLight className="w-4 h-4" />,
    },
    {
      label: t('Delete'),
      onClick: (row: any) => handleDelete(row.uuid, row._title),
      icon: <PiTrashLight className="w-4 h-4" />,
      isDestructive: true,
    },
  ];

  const sortableColumns = ['Name', 'Status', 'Resources', 'Created'];

  // ─── Render ───────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full">
      <Header
        breadcrumbs={[{ name: t('Projects'), href: '/projects', current: true }]}
        actions={[{ label: t('Add Project'), onClick: () => setIsCreating(true), icon: <PiPlusLight className="w-4 h-4" />, variant: 'primary' as const, tourId: 'projects-add-button' }]}
      />

      <div data-tour-id="projects-list">
        <FilterBarWithTabs
          itemCount={items.length}
          totalItemCount={totalItems}
          itemCountLabel="projects"
          filters={filters}
          sortOptions={sortOptions}
          selectedFilters={selectedFilters}
          onFilterChange={handleFilterChange}
          onSortChange={handleSortOptionChange}
          sortValue={`${sortBy === 'created_at' ? 'created' : sortBy}_${sortOrder}`}
          onSearchChange={debouncedSearch}
          onClearAllFilters={handleClearAllFilters}
          actions={filterActions}
          selectionCount={selectedIds.length > 0 ? selectedIds.length : undefined}
          /* Two views: the table, and the stage board. No card grid - a
             project card would repeat the board tile with nothing extra. */
          viewMode={viewMode}
          setViewMode={setViewMode}
          viewModes={['table', 'board']}
        />
      </div>

      {/*
        BOARD VIEW - the same projects as draggable cards under their stage.

        Drag persists through `PATCH /projects/<uuid>/stage`; the move is
        optimistic and snaps back if the call fails, which is what happens
        today on a backend without the `stage` column. The board is the shared
        `StageBoard` the matters list uses - nothing here knows about kanban.
      */}
      {viewMode === 'board' ? (
        <div className="flex-grow min-h-0 flex flex-col pt-2">
          <StageBoard
            columns={boardColumns}
            items={items}
            dragType="project-board-card"
            isLoading={isLoading}
            getId={(project: any) => project.uuid}
            getStage={(project: any) => project.stage}
            onMove={handleBoardMove}
            renderCard={(project: any) => (
              <ProjectBoardCard
                project={project}
                onClick={(uuid) => history.push(`/projects/${uuid}`)}
              />
            )}
          />
        </div>
      ) : (
      /*
        ONE scroller, and it is the table's.

        This box was `flex-grow overflow-y-auto` wrapping a Table that already
        scrolls itself - two nested scroll regions, so the page scrolled the
        table's whole box while the table scrolled its rows. It measures the
        space instead now (`flex-1 min-h-0 flex flex-col`) and the table fills
        it, which is also what hands the table the row the count badge used to
        occupy. The viewport-subtraction cap goes with it: `100vh-120px` was a
        guess at the chrome above, and a guess is exactly what a flex chain
        makes unnecessary.
      */
      <div className="flex-1 min-h-0 flex flex-col relative">
        <Table
          columns={columns}
          data={tableData}
          enableSelection
          selectedIds={selectedIds}
          onSelect={(id: string) => setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])}
          onSelectAll={() => setSelectedIds(selectedIds.length === items.length ? [] : items.map((item: any) => item.uuid))}
          onRowClick={(row: any) => history.push(`/projects/${row.uuid}`)}
          actions={tableActions}
          isLoading={isLoading && items.length === 0}
          isLoadingMore={isLoading && items.length > 0}
          sortableColumns={sortableColumns}
          sortBy={sortBy === 'title' ? 'Name' : sortBy === 'status' ? 'Status' : sortBy === 'resource_count' ? 'Resources' : 'Created'}
          sortOrder={sortOrder}
          onSort={handleSortChange}
          total_items={totalItems}
          total_showing_items={items.length}
          setIsShowing={true}
          showingLabel={t('projects')}
          selectedCount={selectedIds.length}
          resizableColumns
          defaultColumnWidths={{
            'Name': 280,
            'Status': 120,
            'Resources': 100,
            'Access': 180,
            'Owner': 180,
            'Created': 180,
          }}
          storageKeyPrefix="projects"
          max="max-h-none"
          min="min-h-0"
          emptyState={{
            icon: <PiFolderOpenLight />,
            headline: t('No projects yet'),
            subtext: t('Create your first project to start organizing your work.'),
            button: { label: t('Add Project'), onClick: () => setIsCreating(true) },
          }}
        />
      </div>
      )}

      <FeatureTour
        steps={projectsListOrientationSteps}
        run={isProjectsListTourRunning}
        onCallback={handleProjectsListTourCallback}
        continuous
      />

      {/* Audit log slide-over panel */}
      {showAuditPanel && auditProjectUuid && (
        <ActivityDrawer onClose={() => setShowAuditPanel(false)}>
          <AuditLogPanel
            entityType="project"
            entityId={auditProjectUuid}
            entityName={auditProjectTitle}
            onClose={() => setShowAuditPanel(false)}
          />
        </ActivityDrawer>
      )}
    </div>
  );
};

export default ProjectsList;
